import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, EMPTY, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CatPais } from '../interface/catpais.interface';
import { Address } from '../interface/address.interface';
import { ClientData } from '../interface/client.interface';
import { CatIdType } from '../interface/catidtype.interface';
import { ProductTransaction } from '../interface/productTransaction.interface';
import { Product } from '../interface/products/product.interface';
import { ProductDocument } from '../interface/productDocuments.interface';
import { UserLoginResult } from '../interface/userLoginResult.interface';
import { ProductCalculateData } from '../interface/productCalculate.interface';
import { UserData } from '../interface/userData.interface';
import { UserRole } from '../interface/UserRole.interface';
import { UserRegion } from '../interface/UserRegion.interface';
import { Dependent } from '../interface/dependent.interface';
import { Transaction } from '../interface/transaction.interface';
import { PaymentSubscribe } from '../interface/paymentSubscribe.interface';
import { Agency } from '../interface/agency.interface';
import { Affiliate } from '../interface/affiliate.interface';
import { EmployeeBenefits } from '../interface/employeeBenefits.interface';
import { CombinePayment } from '../interface/employer/combinePayment.interface';
import { AddInvoiceResult } from '../interface/employer/traceAR_Result.interface';
import { Provider } from '../interface/products/provider.interface';
import { CoveragePeriod } from '../interface/products/coverageperiod.interface';
import { ProductData } from '../interface/products/ProductData.interface';
import { PaymentMethod } from '../interface/products/paymentMethod.interface';
import { ProductCalculatePrice } from '../interface/products/productCalculation.interface';
import { PaymentPlanCalculation } from '../interface/paymentPlanCalc.interface';
import { DeleteResult } from '../interface/deleteResult.interface';
import { PaymentPlanInput } from '../interface/products/paymentPlanInput.interface';
import { successData } from '../interface/products/successData.interface';
import { productExcluded } from '../interface/products/productsExluded.interface';
import { Catalog } from '../interface/catalog.interface';
import { Product_requiredDocuments } from '../interface/products/product_requiredDocuments.interface';
import { FirstInvoiceResult } from '../interface/traceAR/invoiceResult.interface';
import { PackageName } from '../interface/products/packageName.interface';
import {
  RevenueCurrentMonth,
  ProjectedRevenue,
  OverdueClient,
  NewProgramsByProduct,
  AtRiskPayment,
  CancelledClientsReport,
  CoverageReport,
  ClientCoverageDetail,
  MonthlyCollectionsReport,
  PaymentOverviewReport,
  PaymentOverviewProduct,
  RevenueTrend,
  SellerRankingEntry,
  RevenuePeriod,
  SellerClientTimeline,
  TraceARDashboardReconciliation,
  TraceARPaymentRecordsResponse,
  TraceARPaymentRecordsSummary,
  TraceARPaymentRecordsFilter,
  TraceARPaymentRecordDetail,
  CashReconciliationReport,
  BankStatementUploadResult,
  BankStatementUploadSummary,
  BankStatementLinesPage,
  BankStatementCategory,
  ClientPaymentDetail,
  TracearCaptureDetail,
  VendorBreakdown,
  UnmatchedTracearCapture,
  ReconciliationExplanation,
  ProviderFinancialsRow,
  ProviderFinancialsClient,
} from '../interface/reports.interface';
import { MessagesResponse, UnreadCountResponse } from '../interface/seller-message.interface';
import { PaymentTrackerResponse } from '../interface/sellerPaymentTracker.interface';
import { ProductCommissionRate, ProductCommissionRateInput } from '../interface/products/productCommissionRate.interface';


// "2026-03,2026-04" — mismo formato que espera monthsFilter.js del backend.
function monthsParam(months: { month: number; year: number }[]): string {
  return months.map(m => `${m.year}-${String(m.month).padStart(2, '0')}`).join(',');
}

@Injectable({
  providedIn: 'root'
})
export class DataServiceService {
  private baseUrl = environment.baseUrl

  sessionData: UserLoginResult | null = null;

  constructor(private http: HttpClient) {}

  private get reqHeader(): HttpHeaders {
    const sessionRaw = sessionStorage.getItem('sessionData');
    let token = '';

    if (sessionRaw) {
      try {
        const sessionData = JSON.parse(sessionRaw) as UserLoginResult;
        token = sessionData?.access_token || '';
      } catch {
        token = '';
      }
    }

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  private CALCULATION_PRICETYPE = 'helix'

  private ADDRESS_SERVICE             = '/address'
  private CLIENT_SERVICE              = '/clients'
  private TRANSACTION_SERVICE         = '/transactions'
  private PRODTRANSACTION_SERVICE     = '/product-transactions'
  private PAYMENTS_SERVICE            = '/payments'
  private PRODUCT_SERVICE             = '/products'
  private PROVIDER_SERVICE            = '/providers'
  private PAYMENT_METHOD_SERVICE      = '/payment-methods'
  private DEPENDENT_SERVICE           = '/dependents'
  private AGENCY_SERVICE              = '/agencies'
  private EMPLOYER_SERVICE            = '/employer'
  private CATALOG_SERVICE             = '/catalogs'
  private PRODUCT_EXCLUDES_SERVICE    = '/product-excludes'
  private PRODUCT_PACKAGES_SERVICE    = '/product-packages'
  private PRODUCT_REQUIRED_DOCS_SERVICE = '/product-required-documents'
  private PRODUCT_COMMISSION_RATE_SERVICE = '/product-commission-rates'
  private USERSADMIN_SERVICE          = '/users'
  private USER_SERVICE                = '/auth'
  private STAT_SERVICE                = '/statistics'
  private REPORTS_SERVICE             = '/reports'
  private SELLER_SERVICE              = '/seller'
  private CHAT_SERVICE                = '/chat'


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // ADDRESS  ·  /address
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /address/client/:idClient — all addresses linked to a client */
  getAddressFromClient(idCliente: number): Observable<Address[]> {
    return this.http.get<Address[]>(`${this.baseUrl}${this.ADDRESS_SERVICE}/client/${idCliente}`, { headers: this.reqHeader })
  }

  /** GET /address/:idAddress — single address by PK */
  getAddress(idAddress: number): Observable<Address> {
    return this.http.get<Address>(`${this.baseUrl}${this.ADDRESS_SERVICE}/${idAddress}`, { headers: this.reqHeader })
  }

  /** POST /address — create a new address record */
  storeAddress(address: Address): Observable<Address> {
    return this.http.post<Address>(`${this.baseUrl}${this.ADDRESS_SERVICE}`, address, { headers: this.reqHeader })
  }

  /** PUT /address/:idAddress — update an existing address */
  updateAddress(idAddress: number, address: Address): Observable<Address> {
    return this.http.put<Address>(`${this.baseUrl}${this.ADDRESS_SERVICE}/${idAddress}`, address, { headers: this.reqHeader })
  }

  /** DELETE /address/:idAddress — soft-delete an address */
  deleteAddress(idAddress: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.ADDRESS_SERVICE}/${idAddress}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CLIENTS  ·  /clients
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /clients/assigned-to/:idUser — clients assigned to a seller/user */
  getClientes(idAssignedTo: Number): Observable<ClientData[]> {
    return this.http.get<ClientData[]>(`${this.baseUrl}${this.CLIENT_SERVICE}/assigned-to/${idAssignedTo}`, { headers: this.reqHeader })
  }

  /** GET /clients/assigned-to/:idUser — employee list for an employer user (alias of getClientes) */
  getEmployees(idUser: number): Observable<ClientData[]> {
    return this.http.get<ClientData[]>(`${this.baseUrl}${this.CLIENT_SERVICE}/assigned-to/${idUser}`, { headers: this.reqHeader })
  }

  /** GET /clients — all clients in the system (admin only) */
  getAllClientes(): Observable<ClientData[]> {
    return this.http.get<ClientData[]>(`${this.baseUrl}${this.CLIENT_SERVICE}`, { headers: this.reqHeader })
  }

  /** GET /clients/:idClient — full client record by PK */
  getCliente(idCliente: number): Observable<ClientData> {
    return this.http.get<ClientData>(`${this.baseUrl}${this.CLIENT_SERVICE}/${idCliente}`, { headers: this.reqHeader })
  }

  /** GET /clients/email-check/:email — look up clients by email (duplicate check) */
  getClientsByEmail(email: string): Observable<ClientData[]> {
    const encodedEmail = encodeURIComponent(email.trim())
    return this.http.get<ClientData[]>(`${this.baseUrl}${this.CLIENT_SERVICE}/email-check/${encodedEmail}`, { headers: this.reqHeader })
  }

  /** POST /clients — create a new client record */
  storeCliente(cliente: ClientData): Observable<ClientData> {
    return this.http.post<ClientData>(`${this.baseUrl}${this.CLIENT_SERVICE}`, cliente, { headers: this.reqHeader })
  }

  /** PUT /clients/:idClient — update basic client fields */
  updateCliente(idClient: number, cliente: ClientData): Observable<ClientData> {
    return this.http.put<ClientData>(`${this.baseUrl}${this.CLIENT_SERVICE}/${idClient}`, cliente, { headers: this.reqHeader })
  }

  /** PUT /clients/:idClient — update client and notify active-coverage providers if personal data changed */
  updateClientWithAudit(idClient: number, body: { address?: any, [key: string]: any }): Observable<{ client: ClientData, providerNotified: boolean }> {
    return this.http.put<{ client: ClientData, providerNotified: boolean }>(
      `${this.baseUrl}${this.CLIENT_SERVICE}/${idClient}`, body, { headers: this.reqHeader })
  }

  /** GET /clients/:idClient/active-coverage-providers — providers with active coverage for the client */
  getClientActiveCoverageProviders(idClient: number): Observable<{ hasActiveCoverage: boolean, providers: string[] }> {
    return this.http.get<{ hasActiveCoverage: boolean, providers: string[] }>(
      `${this.baseUrl}${this.CLIENT_SERVICE}/${idClient}/active-coverage-providers`, { headers: this.reqHeader })
  }

  /** DELETE /clients/:idClient — soft-delete a client */
  deleteCliente(idClient: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.CLIENT_SERVICE}/${idClient}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // TRANSACTIONS  ·  /transactions
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /transactions — all transactions; normalizes legacy transactionStatus → lifecycleStatus */
  getTransactions(): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.baseUrl}${this.TRANSACTION_SERVICE}`, { headers: this.reqHeader })
      .pipe(map(txs => txs.map(tx => this.normalizeTransaction(tx))))
  }

  /** GET /transactions/:idTransaction — single transaction by PK */
  getTransactionById(idTransaction: number): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${idTransaction}`, { headers: this.reqHeader })
  }

  /** GET /transactions/user/:idUser — all transactions assigned to a seller */
  getUserTransactions(idUser: number): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/user/${idUser}`, { headers: this.reqHeader })
  }

  /** GET /transactions/user/:idUser/with-payments — seller transactions with payment rows included */
  getUserTransactionsWithPayments(idUser: number): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/user/${idUser}/with-payments`, { headers: this.reqHeader })
  }

  /** GET /transactions/inactive/:status — inactive transactions filtered by a numeric status code */
  getInactiveTransactionsByStatus(status: number): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/inactive/${status}`, { headers: this.reqHeader })
  }

  /** GET /transactions/client/:idClient — all transactions belonging to a client */
  getTransactionsOfClient(idClient: number): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/client/${idClient}`, { headers: this.reqHeader })
  }

  /** POST /transactions — open a new DRAFT transaction for a client */
  createNewTransaction(newTransaction: Transaction): Observable<Transaction> {
    return this.http.post<Transaction>(`${this.baseUrl}${this.TRANSACTION_SERVICE}`, newTransaction, { headers: this.reqHeader })
  }

  /** POST /transactions/:idClient — create an inactive transaction record (legacy enrollment flow) */
  get_InactiveTransaction(idClient: number, transaction: Transaction): Observable<Transaction> {
    return this.http.post<Transaction>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${idClient}`, transaction, { headers: this.reqHeader })
  }

  /** PUT /transactions/:idTransaction — update transaction fields */
  updateTransaction(idTransaction: number, transaction: Transaction): Observable<Transaction> {
    return this.http.put<Transaction>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${idTransaction}`, transaction, { headers: this.reqHeader })
  }

  /** POST /transactions/:idTransaction/coverage-program/store — persist coverage program selection and activate transaction */
  storeCoverageProgram(transaction: Transaction): Observable<Transaction> {
    return this.http.post<Transaction>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${transaction.idTransaction}/coverage-program/store`, transaction, { headers: this.reqHeader })
  }

  /** POST /transactions/:idTransaction/first-invoice — generate and send the first TraceAR invoice for a new coverage program */
  coverageProgramProcessFirstInvoice(transaction: Transaction): Observable<FirstInvoiceResult> {
    return this.http.post<FirstInvoiceResult>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${transaction.idTransaction}/first-invoice`, transaction, { headers: this.reqHeader })
  }

  /** GET /admin/invoice-tracking — full invoice lifecycle for admin monitoring */
  getAdminInvoiceTracking(page = 1, limit = 50): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/admin/invoice-tracking?page=${page}&limit=${limit}`, { headers: this.reqHeader })
  }

  /** GET /version — this environment's backend build info (public, no auth) */
  getVersion(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/version`)
  }

  /** POST /transactions/:idTransaction/coverage-program/cancel — cancel the full coverage program (all product-transactions) */
  cancelCoverageProgram(transaction: Transaction): Observable<{ success: boolean, message: "" }> {
    return this.http.post<{ success: boolean, message: "" }>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${transaction.idTransaction}/coverage-program/cancel`, transaction, { headers: this.reqHeader })
  }

  /** POST /transactions/:idTransaction/coverage-program/cancel — cancel a transaction directly (admin path, no request flow) */
  cancelTransaction(idTransaction: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}${this.TRANSACTION_SERVICE}/${idTransaction}/coverage-program/cancel`,
      {},
      { headers: this.reqHeader }
    )
  }

  /** POST /transactions/:idTransaction/request-cancel — seller submits a cancellation request for admin review */
  requestCancelTransaction(idTransaction: number, reason: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}${this.TRANSACTION_SERVICE}/${idTransaction}/request-cancel`,
      { reason },
      { headers: this.reqHeader }
    )
  }

  /** POST /transactions/employer/employee — bulk-enroll a new employee with benefits (employer flow) */
  createEmployeeWithBenefits(employeeBenefits: EmployeeBenefits): Observable<EmployeeBenefits> {
    return this.http.post<EmployeeBenefits>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/employer/employee`, employeeBenefits, { headers: this.reqHeader })
  }

  /** DELETE /transactions/:idTransaction — hard-delete a DRAFT transaction */
  deleteTransaction(idTransaction: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.TRANSACTION_SERVICE}/${idTransaction}`, { headers: this.reqHeader })
  }

  /** Transitional normalization: maps legacy transactionStatus → lifecycleStatus until backend serializers are deployed */
  private normalizeTransaction(raw: any): Transaction {
    return {
      ...raw,
      lifecycleStatus: raw.lifecycleStatus ?? raw.transactionStatus,
      coverageStatus:  raw.coverageStatus ?? undefined,
    }
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCT TRANSACTIONS  ·  /product-transactions
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** POST /product-transactions — upsert a product-transaction (create or replace if exists) */
  storeOrUpdateProductTransaction(productTransaction: ProductTransaction): Observable<ProductTransaction> {
    return this.http.post<ProductTransaction>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}`, productTransaction, { headers: this.reqHeader })
  }

  /** POST /product-transactions — alias used during coverage program build step (same endpoint as storeOrUpdateProductTransaction) */
  storeCoverageProgramItem(productTransaction: ProductTransaction): Observable<ProductTransaction> {
    return this.http.post<ProductTransaction>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}`, productTransaction, { headers: this.reqHeader })
  }

  /** PUT /product-transactions/:idProductTransaction — update fields on an existing product-transaction */
  updateCoverageProgramItem(idProductTransaction: number, productTransaction: ProductTransaction): Observable<ProductTransaction> {
    return this.http.put<ProductTransaction>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/${idProductTransaction}`, productTransaction, { headers: this.reqHeader })
  }

  /** GET /product-transactions/transaction/:idTransaction/product/:idProduct — single PT by transaction+product composite key */
  getProductTransaction(idTransaction: number, idProduct: number): Observable<ProductTransaction> {
    return this.http.get<ProductTransaction>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/transaction/${idTransaction}/product/${idProduct}`, { headers: this.reqHeader })
  }

  /** GET /product-transactions/transaction/:idTransaction/user/:idUser — all PTs for a transaction scoped to a user */
  getClientProductsFromTransaction(idUser: number, idTransaction: number): Observable<ProductTransaction[]> {
    return this.http.get<ProductTransaction[]>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/transaction/${idTransaction}/user/${idUser}`, { headers: this.reqHeader })
  }

  /** GET /product-transactions/by-status/:idProduct/:transactionStatus — PTs for a product filtered by transaction status */
  getProductsFromInactiveTransactionByStatus(idProduct: number, transactionStatus: number): Observable<ProductTransaction[]> {
    return this.http.get<ProductTransaction[]>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/by-status/${idProduct}/${transactionStatus}`, { headers: this.reqHeader })
  }

  /** POST /product-transactions/:idProductTransaction/cancel — admin cancels a single product line within a transaction */
  cancelProductTransaction(idProductTransaction: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/${idProductTransaction}/cancel`,
      {},
      { headers: this.reqHeader }
    )
  }

  /** POST /product-transactions/:idProductTransaction/request-cancel — seller requests cancellation of a single product line */
  requestCancelProductTransaction(idProductTransaction: number, reason: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/${idProductTransaction}/request-cancel`,
      { reason },
      { headers: this.reqHeader }
    )
  }

  /** GET /product-transactions/calculate/:idProduct/:paymentOption/:startDate/:endDate/:numDependents — legacy price calculation (prefer /calculations/price) */
  calculateProduct(idProduct: number, paymentOption: number, startDate: string, endDate: string, numDependents: number): Observable<ProductCalculateData> {
    return this.http.get<ProductCalculateData>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/calculate/${idProduct}/${paymentOption}/${startDate}/${endDate}/${numDependents}`, { headers: this.reqHeader })
  }

  // — Product Documents (nested under /product-transactions) —

  /** GET /product-transactions/:idProductTransaction/documents/:documentType — retrieve a specific document attached to a PT */
  getProductDocument(idProductClient: number, documentType: string): Observable<ProductDocument> {
    return this.http.get<ProductDocument>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/${idProductClient}/documents/${documentType}`, { headers: this.reqHeader })
  }

  /** POST /product-transactions/:idProductTransaction/documents — attach or replace a document on a PT */
  storeOrUpdateProductDocument(document: ProductDocument): Observable<ProductDocument> {
    return this.http.post<ProductDocument>(`${this.baseUrl}${this.PRODTRANSACTION_SERVICE}/${document.idProductTransaction}/documents`, document, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PAYMENTS  ·  /payments
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /payments/by-product-transaction/:idProductTransaction — payment schedule for a single product line */
  getProductPayments(idProductTransaction: number): Observable<PaymentSubscribe[]> {
    return this.http.get<PaymentSubscribe[]>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/by-product-transaction/${idProductTransaction}`, { headers: this.reqHeader })
  }

  /** GET /payments/by-user/:assignedTo — all payments across the portfolio of an assigned user */
  getPaymentsFromAssignedUser(assignedTo: number): Observable<PaymentSubscribe[]> {
    return this.http.get<PaymentSubscribe[]>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/by-user/${assignedTo}`, { headers: this.reqHeader })
  }

  /** GET /payments/by-transaction/:idTransaction — all payments belonging to a transaction */
  getPaymentsOfTransaction(idTransaction: number): Observable<PaymentSubscribe[]> {
    return this.http.get<PaymentSubscribe[]>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/by-transaction/${idTransaction}`, { headers: this.reqHeader })
  }

  /** GET /payments/first-by-transaction/:idTransaction — the first scheduled payment for a transaction (used to determine UPCOMING state) */
  getTransactionFirstPayment(idTransaction: number): Observable<PaymentSubscribe> {
    return this.http.get<PaymentSubscribe>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/first-by-transaction/${idTransaction}`, { headers: this.reqHeader })
  }

  /** POST /payments/batch — bulk-create a payment plan (array of PaymentSubscribe rows) */
  storePaymentPlan(payments: PaymentSubscribe[]): Observable<PaymentSubscribe[]> {
    return this.http.post<PaymentSubscribe[]>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/batch`, payments, { headers: this.reqHeader })
  }

  /** DELETE /payments/by-product-transaction/:idProductTransaction — delete all payment rows for a product line */
  deletePaymentsPlan(idProductTransaction: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/by-product-transaction/${idProductTransaction}`, { headers: this.reqHeader })
  }

  /** POST /payments/execute/:idClient — trigger manual payment execution for a client */
  makePayment(idClient: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/execute/${idClient}`, { idClient }, { headers: this.reqHeader })
  }

  /** POST /payments/:idPayment/reconcile — admin marks a payment as reconciled (paid) */
  reconcilePayment(idPayment: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}${this.PAYMENTS_SERVICE}/${idPayment}/reconcile`, null, { headers: this.reqHeader })
  }

  /** POST /payments/:idPayment/refund — admin processes a refund for a paid payment */
  refundPayment(idPayment: number, reason?: string | null): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}${this.PAYMENTS_SERVICE}/${idPayment}/refund`,
      { reason: reason ?? null },
      { headers: this.reqHeader }
    )
  }

  /** POST /payments/:idPayment/charge — admin forces immediate TraceAR charge for a recurring invoice */
  chargePayment(idPayment: number): Observable<{ success: boolean; idPayment: number; message?: string; reason?: string }> {
    return this.http.post<any>(
      `${this.baseUrl}${this.PAYMENTS_SERVICE}/${idPayment}/charge`,
      null,
      { headers: this.reqHeader }
    )
  }

  /** POST /payments/:idPayment/request-refund — seller requests a refund for a payment */
  requestPaymentRefund(idPayment: number, reason: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}${this.PAYMENTS_SERVICE}/${idPayment}/request-refund`,
      { reason },
      { headers: this.reqHeader }
    )
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCTS  ·  /products
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /products — all products (including inactive) */
  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.baseUrl}${this.PRODUCT_SERVICE}`, { headers: this.reqHeader })
  }

  /** GET /products/active — only active (isActive=true) products */
  getActiveProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.baseUrl}${this.PRODUCT_SERVICE}/active`, { headers: this.reqHeader })
  }

  /** GET /products/:idProduct — single product by PK */
  getProductById(idProduct: number): Observable<Product> {
    return this.http.get<Product>(`${this.baseUrl}${this.PRODUCT_SERVICE}/${idProduct}`, { headers: this.reqHeader })
  }

  /** PUT /products/:idProduct — update product metadata (name, pricing, etc.) */
  updateProduct(idProduct: number, productData: ProductData): Observable<ProductData> {
    return this.http.put<ProductData>(`${this.baseUrl}${this.PRODUCT_SERVICE}/${idProduct}`, productData, { headers: this.reqHeader })
  }

  /** PUT /products/:idProduct/status/:activeStatus — toggle a product active/inactive */
  updateProductActiveStatus(idProduct: number, activeStatus: boolean): Observable<Product> {
    return this.http.put<Product>(`${this.baseUrl}${this.PRODUCT_SERVICE}/${idProduct}/status/${activeStatus}`, {}, { headers: this.reqHeader })
  }

  /** POST /products — create a new product */
  createProduct(productData: ProductData): Observable<ProductData> {
    return this.http.post<ProductData>(`${this.baseUrl}${this.PRODUCT_SERVICE}`, productData, { headers: this.reqHeader })
  }

  /** DELETE /products/:idProduct — remove a product (only allowed if no active coverage programs reference it) */
  deleteProduct(idProduct: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PRODUCT_SERVICE}/${idProduct}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCT COMMISSION RATES  ·  /product-commission-rates
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /product-commission-rates?idProduct= — full rate history, optionally filtered by product */
  getProductCommissionRates(idProduct?: number): Observable<ProductCommissionRate[]> {
    const url = idProduct
      ? `${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}?idProduct=${idProduct}`
      : `${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}`
    return this.http.get<ProductCommissionRate[]>(url, { headers: this.reqHeader })
  }

  /** GET /product-commission-rates/:id — single rate by PK */
  getProductCommissionRateById(id: number): Observable<ProductCommissionRate> {
    return this.http.get<ProductCommissionRate>(`${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}/${id}`, { headers: this.reqHeader })
  }

  /** POST /product-commission-rates — define a new rate; closes the currently open one for that product */
  createProductCommissionRate(data: ProductCommissionRateInput): Observable<ProductCommissionRate> {
    return this.http.post<ProductCommissionRate>(`${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}`, data, { headers: this.reqHeader })
  }

  /** PUT /product-commission-rates/:id — correct fields on an existing rate record */
  updateProductCommissionRate(id: number, data: Partial<ProductCommissionRateInput>): Observable<ProductCommissionRate> {
    return this.http.put<ProductCommissionRate>(`${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}/${id}`, data, { headers: this.reqHeader })
  }

  /** DELETE /product-commission-rates/:id */
  deleteProductCommissionRate(id: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PRODUCT_COMMISSION_RATE_SERVICE}/${id}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PROVIDERS  ·  /providers
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /providers — all insurance/benefit providers */
  getProviders(): Observable<Provider[]> {
    return this.http.get<Provider[]>(`${this.baseUrl}${this.PROVIDER_SERVICE}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PAYMENT METHODS  ·  /payment-methods
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /payment-methods — all available payment methods (ACH, check, etc.) */
  getPaymentMethods(): Observable<PaymentMethod[]> {
    return this.http.get<PaymentMethod[]>(`${this.baseUrl}${this.PAYMENT_METHOD_SERVICE}`, { headers: this.reqHeader })
  }

  /** GET /payment-methods/:idPaymentMethod — single payment method by PK */
  getPaymentMethodById(idPaymentMethod: number): Observable<PaymentMethod> {
    return this.http.get<PaymentMethod>(`${this.baseUrl}${this.PAYMENT_METHOD_SERVICE}/${idPaymentMethod}`, { headers: this.reqHeader })
  }

  /** PUT /payment-methods/:idPaymentMethod — update a payment method record */
  updatePaymentMethod(idPaymentMethod: number, paymentMethod: PaymentMethod): Observable<PaymentMethod> {
    return this.http.put<PaymentMethod>(`${this.baseUrl}${this.PAYMENT_METHOD_SERVICE}/${idPaymentMethod}`, paymentMethod, { headers: this.reqHeader })
  }

  /** POST /payment-methods — create a new payment method */
  createPaymentMethod(paymentMethod: PaymentMethod): Observable<PaymentMethod> {
    return this.http.post<PaymentMethod>(`${this.baseUrl}${this.PAYMENT_METHOD_SERVICE}`, paymentMethod, { headers: this.reqHeader })
  }

  /** DELETE /payment-methods/:idPaymentMethod — delete a payment method */
  deletePaymentMethod(idPaymentMethod: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PAYMENT_METHOD_SERVICE}/${idPaymentMethod}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CALCULATIONS  ·  /calculations
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** POST /calculations/price — compute the price for a product given dates, payment method, and dependent count */
  calculateProductPrice(contractDate: string, starDate: string, endDate: string, product: Product, paymentMethod: number, numDependants: number): Observable<ProductCalculatePrice> {
    let query = `contractDate=${contractDate}&startDate=${starDate}&endDate=${endDate}&paymentMethod=${paymentMethod}&numDependants=${numDependants}&priceType=${this.CALCULATION_PRICETYPE}`
    return this.http.post<ProductCalculatePrice>(`${this.baseUrl}/calculations/price?${query}`, product, { headers: this.reqHeader })
  }

  /** GET /calculations/ready-check — validate that the given date range is eligible for a coverage calculation */
  checkIfReadyToCalculate(contractDate: string, startDate: string, endDate: string): Observable<successData> {
    let query = `contractDate=${contractDate}&startDate=${startDate}&endDate=${endDate}`
    return this.http.get<successData>(`${this.baseUrl}/calculations/ready-check?${query}`, { headers: this.reqHeader })
  }

  /** POST /calculations/payment-plan — generate the full payment plan schedule for an enrollment */
  getPaymentPlan(contractDate: string, startDate: string, endDate: string, numDependants: number, product: Product, paymentMethod: number): Observable<PaymentPlanCalculation> {
    let paymentPlanInput: PaymentPlanInput = {
      contractDate: new Date(contractDate),
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      numDependants,
      paymentMethod,
      product,
      priceType: this.CALCULATION_PRICETYPE
    }
    return this.http.post<PaymentPlanCalculation>(`${this.baseUrl}/calculations/payment-plan`, paymentPlanInput, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // DEPENDENTS  ·  /dependents
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /dependents/transaction/:idTransaction — dependents enrolled under a specific transaction */
  getDependentsOfClient(idTransaction: number): Observable<Dependent[]> {
    return this.http.get<Dependent[]>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/transaction/${idTransaction}`, { headers: this.reqHeader })
  }

  /** GET /dependents/client/:idClient — all dependents across every transaction of a client */
  getDependentsFromAllTransactions(idClient: number): Observable<Dependent[]> {
    return this.http.get<Dependent[]>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/client/${idClient}`, { headers: this.reqHeader })
  }

  /** POST /dependents — add a new dependent to a transaction */
  storeDependent(dependent: Dependent): Observable<Dependent> {
    return this.http.post<Dependent>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/`, dependent, { headers: this.reqHeader })
  }

  /** POST /dependents/transfer/one — move a dependent from one transaction to another */
  transferOneDependentToAnotherTransaction(dependent: Dependent, idNewTransaction: number): Observable<{ dependent: Dependent, transaction: Transaction }> {
    return this.http.post<{ dependent: Dependent, transaction: Transaction }>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/transfer/one`, { dependent, idTransaction: idNewTransaction }, { headers: this.reqHeader })
  }

  /** PUT /dependents/:idDependent — update a dependent's information */
  updateDependent(idDependent: number, dependent: Dependent): Observable<Dependent> {
    return this.http.put<Dependent>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/${idDependent}`, dependent, { headers: this.reqHeader })
  }

  /** DELETE /dependents/:idDependent — remove a dependent from a transaction */
  deleteDependent(idDependent: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.DEPENDENT_SERVICE}/${idDependent}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // AGENCIES & AFFILIATES  ·  /agencies
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /agencies — all agencies in the system */
  getAgencies(): Observable<Agency[]> {
    return this.http.get<Agency[]>(`${this.baseUrl}${this.AGENCY_SERVICE}`, { headers: this.reqHeader })
  }

  /** GET /agencies/:idAgency — single agency by PK */
  getAgencyById(idAgency: number): Observable<Agency> {
    return this.http.get<Agency>(`${this.baseUrl}${this.AGENCY_SERVICE}/${idAgency}`, { headers: this.reqHeader })
  }

  /** GET /agencies/email/:email — look up an agency by its email address (no v2 equivalent yet) */
  getAgencyByEmail(email: string): Observable<Agency> {
    return this.http.get<Agency>(`${this.baseUrl}${this.AGENCY_SERVICE}/email/${email}`, { headers: this.reqHeader })
  }

  /** POST /agencies — create a new agency */
  storeAgency(agency: Agency): Observable<Agency> {
    return this.http.post<Agency>(`${this.baseUrl}${this.AGENCY_SERVICE}`, agency, { headers: this.reqHeader })
  }

  /** PUT /agencies/:idAgency — update agency data */
  updateAgency(idAgency: number, agency: Agency): Observable<Agency> {
    return this.http.put<Agency>(`${this.baseUrl}${this.AGENCY_SERVICE}/${idAgency}`, agency, { headers: this.reqHeader })
  }

  /** DELETE /agencies/:idAgency — soft-delete an agency */
  deleteAgency(idAgency: number): Observable<any> {
    return this.http.delete<Agency>(`${this.baseUrl}${this.AGENCY_SERVICE}/${idAgency}`, { headers: this.reqHeader })
  }

  /** GET /agencies/:idAgency/affiliates — affiliates belonging to an agency */
  getAffiliates(idAgency: number): Observable<Affiliate[]> {
    return this.http.get<Affiliate[]>(`${this.baseUrl}${this.AGENCY_SERVICE}/${idAgency}/affiliates`, { headers: this.reqHeader })
  }

  /** GET /agencies/affiliates/email/:email — look up affiliates by email (no v2 equivalent yet) */
  getAffiliateByEmail(email: string): Observable<Affiliate[]> {
    return this.http.get<Affiliate[]>(`${this.baseUrl}${this.AGENCY_SERVICE}/affiliates/email/${email}`, { headers: this.reqHeader })
  }

  /** POST /agencies/affiliates — create a new affiliate under an agency */
  storeAffiliate(affiliate: Affiliate): Observable<Affiliate> {
    return this.http.post<Affiliate>(`${this.baseUrl}${this.AGENCY_SERVICE}/affiliates`, affiliate, { headers: this.reqHeader })
  }

  /** PUT /agencies/affiliates/:idAffiliate — update affiliate data */
  updateAffiliate(idAffiliate: number, affiliate: Affiliate): Observable<Affiliate> {
    return this.http.put<Affiliate>(`${this.baseUrl}${this.AGENCY_SERVICE}/affiliates/${idAffiliate}`, affiliate, { headers: this.reqHeader })
  }

  /** DELETE /agencies/affiliates/:idAffiliate — soft-delete an affiliate */
  deleteAffiliate(idAffiliate: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}${this.AGENCY_SERVICE}/affiliates/${idAffiliate}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // EMPLOYER  ·  /employer
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** POST /employer/combine-payments — create a bulk-payment envelope for an employer group */
  createCombinePayment(combinePayment: CombinePayment): Observable<CombinePayment> {
    return this.http.post<CombinePayment>(`${this.baseUrl}${this.EMPLOYER_SERVICE}/combine-payments`, combinePayment, { headers: this.reqHeader })
  }

  /** GET /employer/combine-payments/user/:idUser — all combine-payment envelopes for an employer user */
  getCombinePaymentsFromEmployer(idUser: number): Observable<CombinePayment[]> {
    return this.http.get<CombinePayment[]>(`${this.baseUrl}${this.EMPLOYER_SERVICE}/combine-payments/user/${idUser}`, { headers: this.reqHeader })
  }

  /** GET /employer/combine-payments/:idCombinePayment — single combine-payment envelope by PK */
  getCombinePayment(idCombinePayment: number): Observable<CombinePayment> {
    return this.http.get<CombinePayment>(`${this.baseUrl}${this.EMPLOYER_SERVICE}/combine-payments/${idCombinePayment}`, { headers: this.reqHeader })
  }

  /** POST /employer/combine-payments/invoice — generate a TraceAR invoice for a combine-payment envelope */
  invoiceInitialCombinePayment(combinePayment: CombinePayment): Observable<AddInvoiceResult> {
    return this.http.post<AddInvoiceResult>(`${this.baseUrl}${this.EMPLOYER_SERVICE}/combine-payments/invoice`, combinePayment, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CATALOGS  ·  /catalogs
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /catalogs/countries — list of all countries (CatPais) */
  getCatPaises(): Observable<CatPais[]> {
    return this.http.get<CatPais[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/countries`, { headers: this.reqHeader })
  }

  /** GET /catalogs/countries/:idPais — single country record */
  getPais(idPais: number): Observable<CatPais> {
    return this.http.get<CatPais>(`${this.baseUrl}${this.CATALOG_SERVICE}/countries/${idPais}`, { headers: this.reqHeader })
  }

  /** GET /catalogs/id-types — government ID types for client identification */
  getCatIdType(): Observable<CatIdType[]> {
    return this.http.get<CatIdType[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/id-types`, { headers: this.reqHeader })
  }

  /** GET /catalogs/periods — coverage period options (monthly, quarterly, etc.) */
  getCoveragePeriod(): Observable<CoveragePeriod[]> {
    return this.http.get<CoveragePeriod[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/periods`, { headers: this.reqHeader })
  }

  /** GET /catalogs/user-roles — all user role definitions */
  getSystemUserRole(): Observable<UserRole[]> {
    return this.http.get<UserRole[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/user-roles`, { headers: this.reqHeader })
  }

  /** GET /catalogs/user-regions — all geographic regions a user can be assigned to */
  getSystemUserRegion(): Observable<UserRegion[]> {
    return this.http.get<UserRegion[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/user-regions`, { headers: this.reqHeader })
  }

  /** GET /catalogs/definitions/area/:area — dynamic catalog entries filtered by area code */
  getCatalogsByArea(area: string): Observable<Catalog[]> {
    return this.http.get<Catalog[]>(`${this.baseUrl}${this.CATALOG_SERVICE}/definitions/area/${area}`, { headers: this.reqHeader })
  }

  /** GET /catalogs/definitions/:idCatalog — single catalog entry by PK */
  getCatalogById(idCatalog: number): Observable<Catalog> {
    return this.http.get<Catalog>(`${this.baseUrl}${this.CATALOG_SERVICE}/definitions/${idCatalog}`, { headers: this.reqHeader })
  }

  /** POST /catalogs/definitions — add a new catalog entry */
  storeCatalog(catalog: Catalog): Observable<Catalog> {
    return this.http.post<Catalog>(`${this.baseUrl}${this.CATALOG_SERVICE}/definitions`, catalog, { headers: this.reqHeader })
  }

  /** PUT /catalogs/definitions/:idCatalog — update an existing catalog entry */
  updateCatalog(idCatalog: number, catalog: Catalog): Observable<Catalog> {
    return this.http.put<Catalog>(`${this.baseUrl}${this.CATALOG_SERVICE}/definitions/${idCatalog}`, catalog, { headers: this.reqHeader })
  }

  /** DELETE /catalogs/definitions/:idCatalog — remove a catalog entry */
  deleteCatalog(idCatalog: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.CATALOG_SERVICE}/definitions/${idCatalog}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCT EXCLUDES  ·  /product-excludes
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /product-excludes/by-product/:idProduct — products that cannot be combined with this product */
  getProductExcluded(idProduct: number): Observable<productExcluded[]> {
    return this.http.get<productExcluded[]>(`${this.baseUrl}${this.PRODUCT_EXCLUDES_SERVICE}/by-product/${idProduct}`, { headers: this.reqHeader })
  }

  /** POST /product-excludes — define a mutual exclusion between two products */
  excludeProduct(productExcluded: productExcluded): Observable<productExcluded> {
    return this.http.post<productExcluded>(`${this.baseUrl}${this.PRODUCT_EXCLUDES_SERVICE}`, productExcluded, { headers: this.reqHeader })
  }

  /** DELETE /product-excludes/:idProductExclude — remove a product exclusion rule */
  excludeProductRemove(idProductExclude: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PRODUCT_EXCLUDES_SERVICE}/${idProductExclude}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCT PACKAGES  ·  /product-packages
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /product-packages/names — all package name definitions */
  getAllPackageNames(): Observable<PackageName[]> {
    return this.http.get<PackageName[]>(`${this.baseUrl}${this.PRODUCT_PACKAGES_SERVICE}/names`, { headers: this.reqHeader })
  }

  /** GET /product-packages/names/:idPackageName — single package name by PK */
  getPackageNameById(idPackageName: number): Observable<PackageName> {
    return this.http.get<PackageName>(`${this.baseUrl}${this.PRODUCT_PACKAGES_SERVICE}/names/${idPackageName}`, { headers: this.reqHeader })
  }

  /** POST /product-packages/names — create a new package name */
  savePackageName(packageName: PackageName): Observable<PackageName> {
    return this.http.post<PackageName>(`${this.baseUrl}${this.PRODUCT_PACKAGES_SERVICE}/names`, packageName, { headers: this.reqHeader })
  }

  /** PUT /product-packages/names/:idPackageName — update a package name */
  updatePackageName(idPackageName: number, packageName: PackageName): Observable<PackageName> {
    return this.http.put<PackageName>(`${this.baseUrl}${this.PRODUCT_PACKAGES_SERVICE}/names/${idPackageName}`, packageName, { headers: this.reqHeader })
  }

  /** DELETE /product-packages/names/:idPackageName — delete a package name */
  deletePackageName(idPackageName: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PRODUCT_PACKAGES_SERVICE}/names/${idPackageName}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PRODUCT REQUIRED DOCUMENTS  ·  /product-required-documents
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** POST /product-required-documents — declare a document type as required for a product */
  addDocumentRequired(requiredDocument: Product_requiredDocuments): Observable<Product_requiredDocuments> {
    return this.http.post<Product_requiredDocuments>(`${this.baseUrl}${this.PRODUCT_REQUIRED_DOCS_SERVICE}`, requiredDocument, { headers: this.reqHeader });
  }

  /** DELETE /product-required-documents/:idProductRequiredDocument — remove a required-document rule from a product */
  removeDocumentRequired(idProductRequiredDocument: number): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(`${this.baseUrl}${this.PRODUCT_REQUIRED_DOCS_SERVICE}/${idProductRequiredDocument}`, { headers: this.reqHeader });
  }

  /** GET /product-required-documents/by-product/:idProduct — list of documents required for a given product */
  getDocumentsRequiredByProduct(idProduct: number): Observable<Product_requiredDocuments[]> {
    return this.http.get<Product_requiredDocuments[]>(`${this.baseUrl}${this.PRODUCT_REQUIRED_DOCS_SERVICE}/by-product/${idProduct}`, { headers: this.reqHeader });
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // USERS  ·  /users
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /users — all system users; only callable by securityLevel=4 (super-admin), returns empty otherwise */
  getSystemUsers(): Observable<UserData[]> {
    let sessionData: UserLoginResult = JSON.parse(sessionStorage.getItem('sessionData')!)
    let user: UserData = sessionData.response.user
    if (user.securityLevel == 4) {
      return this.http.get<UserData[]>(`${this.baseUrl}${this.USERSADMIN_SERVICE}`, { headers: this.reqHeader })
    } else {
      return EMPTY.pipe(startWith([]))
    }
  }

  /** GET /users/:idUser — single system user by PK */
  getSystemUserById(idUser: number): Observable<UserData> {
    return this.http.get<UserData>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/${idUser}`, { headers: this.reqHeader })
  }

  /** POST /users/register — create a new system user account */
  storeSystemUser(user: UserData): Observable<UserData> {
    return this.http.post<UserData>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/register`, user, { headers: this.reqHeader })
  }

  /** POST /users/invite — generate an affiliate invite code for a user */
  createAffiliateInvite(idUser: number): Observable<{ success: boolean; code: string }> {
    return this.http.post<{ success: boolean; code: string }>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/invite`, { idUser }, { headers: this.reqHeader })
  }

  /** GET /users/:idUser/invite — retrieve the current affiliate invite code for a user */
  getMyAffiliateInvite(idUser: number): Observable<{ success: boolean; invite: { code: string } | null }> {
    return this.http.get<{ success: boolean; invite: { code: string } | null }>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/${idUser}/invite`, { headers: this.reqHeader })
  }

  /** POST /users/:idUser/update-password — change a user's password (currentPassword required for self-service) */
  updateUserPassword(idUser: number, body: { newPassword: string; currentPassword?: string }): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}${this.USERSADMIN_SERVICE}/${idUser}/update-password`,
      body,
      { headers: this.reqHeader }
    )
  }

  /** PUT /users/:idUser — update user profile fields */
  updateSystemUser(idUser: number, user: Partial<UserData>): Observable<UserData> {
    return this.http.put<UserData>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/${idUser}`, user, { headers: this.reqHeader })
  }

  /** PATCH /users/:idUser/deactivate — disable a user account without deleting it */
  deactivateSystemUser(idUser: number): Observable<{ success: boolean }> {
    return this.http.patch<{ success: boolean }>(`${this.baseUrl}${this.USERSADMIN_SERVICE}/${idUser}/deactivate`, {}, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // STATISTICS  ·  /statistics
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /statistics/client-plans/inactive/:idUser — count of inactive plans per status for a user's portfolio */
  countUserInactivePlansByStatus(idUser: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}${this.STAT_SERVICE}/client-plans/inactive/${idUser}`, { headers: this.reqHeader })
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // REPORTS  ·  /reports
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /reports/revenue/current-month — total collected revenue for the current calendar month */
  getRevenueCurrentMonth(): Observable<RevenueCurrentMonth> {
    return this.http.get<RevenueCurrentMonth>(`${this.baseUrl}${this.REPORTS_SERVICE}/revenue/current-month`, { headers: this.reqHeader });
  }

  /** GET /reports/revenue/projected — projected revenue broken down by upcoming payment dates */
  getRevenueProjected(): Observable<ProjectedRevenue[]> {
    return this.http.get<ProjectedRevenue[]>(`${this.baseUrl}${this.REPORTS_SERVICE}/revenue/projected`, { headers: this.reqHeader });
  }

  /** GET /reports/clients/overdue — clients with at least one overdue payment */
  getClientsOverdue(): Observable<OverdueClient[]> {
    return this.http.get<OverdueClient[]>(`${this.baseUrl}${this.REPORTS_SERVICE}/clients/overdue`, { headers: this.reqHeader });
  }

  /** GET /reports/clients/new-programs — new coverage programs grouped by product for a given month/year */
  getClientsNewPrograms(month: number, year: number): Observable<NewProgramsByProduct[]> {
    return this.http.get<NewProgramsByProduct[]>(
      `${this.baseUrl}${this.REPORTS_SERVICE}/clients/new-programs?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /reports/clients/at-risk — clients with upcoming payments likely to be missed */
  getClientsAtRisk(): Observable<AtRiskPayment[]> {
    return this.http.get<AtRiskPayment[]>(`${this.baseUrl}${this.REPORTS_SERVICE}/clients/at-risk`, { headers: this.reqHeader });
  }

  /** GET /reports/clients/cancelled — cancelled clients; optionally filtered by month/year */
  getClientsCancelled(month?: number, year?: number): Observable<CancelledClientsReport> {
    const params = (month != null && year != null) ? `?month=${month}&year=${year}` : '';
    return this.http.get<CancelledClientsReport>(`${this.baseUrl}${this.REPORTS_SERVICE}/clients/cancelled${params}`, { headers: this.reqHeader });
  }

  /** GET /reports/coverage — coverage summary report for a specific month/year */
  getCoverageReport(month: number, year: number): Observable<CoverageReport> {
    return this.http.get<CoverageReport>(
      `${this.baseUrl}${this.REPORTS_SERVICE}/coverage?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /reports/coverage/client-detail — per-client coverage detail for a specific month/year */
  getCoverageClientDetail(idClient: number, month: number, year: number): Observable<ClientCoverageDetail> {
    return this.http.get<ClientCoverageDetail>(
      `${this.baseUrl}${this.REPORTS_SERVICE}/coverage/client-detail?idClient=${idClient}&month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /reports/collections — paid invoices grouped by scheduled payment month */
  getMonthlyCollections(month: number, year: number): Observable<MonthlyCollectionsReport> {
    return this.http.get<MonthlyCollectionsReport>(
      `${this.baseUrl}${this.REPORTS_SERVICE}/collections?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/payment-overview — combined HELIX + Employer System payment report (admin only) */
  getPaymentOverview(month: number, year: number): Observable<PaymentOverviewReport> {
    return this.http.get<PaymentOverviewReport>(
      `${this.baseUrl}/admin/payment-overview?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/payment-overview/pdf — download the payment overview report as a PDF blob */
  downloadPaymentOverviewPdf(month: number, year: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/admin/payment-overview/pdf?month=${month}&year=${year}`,
      { headers: this.reqHeader, responseType: 'blob' }
    );
  }

  /** GET /admin/business-intelligence/revenue-trend — 13-month collected vs. expected trend */
  getRevenueTrend(): Observable<RevenueTrend> {
    return this.http.get<RevenueTrend>(
      `${this.baseUrl}/admin/business-intelligence/revenue-trend`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/business-intelligence/seller-ranking — sellers ranked by amount collected in the month */
  getSellerRanking(month: number, year: number): Observable<SellerRankingEntry[]> {
    return this.http.get<SellerRankingEntry[]>(
      `${this.baseUrl}/admin/business-intelligence/seller-ranking?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/business-intelligence/seller-ranking-by-period — sellers ranked over a rolling window (quarter/half/year) ending in the given month */
  getSellerRankingByPeriod(period: RevenuePeriod, month: number, year: number): Observable<SellerRankingEntry[]> {
    return this.http.get<SellerRankingEntry[]>(
      `${this.baseUrl}/admin/business-intelligence/seller-ranking-by-period?period=${period}&month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/business-intelligence/product-breakdown-by-period — revenue by product over a rolling window (quarter/half/year) ending in the given month */
  getProductBreakdownByPeriod(period: RevenuePeriod, month: number, year: number): Observable<PaymentOverviewProduct[]> {
    return this.http.get<PaymentOverviewProduct[]>(
      `${this.baseUrl}/admin/business-intelligence/product-breakdown-by-period?period=${period}&month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/business-intelligence/seller-client-timeline — clients whose coverage started each month, grouped by seller (trailing 12 months) */
  getSellerClientTimeline(): Observable<SellerClientTimeline> {
    return this.http.get<SellerClientTimeline>(
      `${this.baseUrl}/admin/business-intelligence/seller-client-timeline`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/business-intelligence/tracear-reconciliation — TraceAR-confirmed cash for the month + sync health */
  getTraceARReconciliation(month: number, year: number): Observable<TraceARDashboardReconciliation> {
    return this.http.get<TraceARDashboardReconciliation>(
      `${this.baseUrl}/admin/business-intelligence/tracear-reconciliation?month=${month}&year=${year}`,
      { headers: this.reqHeader }
    );
  }

  /** GET /admin/tracear-payments — raw audit list of synced tracear_payment_record rows */
  getTraceARPaymentRecords(filter: TraceARPaymentRecordsFilter): Observable<TraceARPaymentRecordsResponse> {
    let params = new HttpParams();
    if (filter.page)      params = params.set('page', filter.page);
    if (filter.limit)     params = params.set('limit', filter.limit);
    if (filter.status)    params = params.set('status', filter.status);
    if (filter.matchSource) params = params.set('matchSource', filter.matchSource);
    if (filter.search)    params = params.set('search', filter.search);
    if (filter.startDate) params = params.set('startDate', filter.startDate);
    if (filter.endDate)   params = params.set('endDate', filter.endDate);

    return this.http.get<TraceARPaymentRecordsResponse>(
      `${this.baseUrl}/admin/tracear-payments`,
      { headers: this.reqHeader, params }
    );
  }

  /** GET /admin/tracear-payments/summary — total/matched/unmatched + by-status/by-match sums, scoped to the same filters as getTraceARPaymentRecords */
  getTraceARPaymentRecordsSummary(filter?: Omit<TraceARPaymentRecordsFilter, 'page' | 'limit'>): Observable<TraceARPaymentRecordsSummary> {
    let params = new HttpParams();
    if (filter?.status)    params = params.set('status', filter.status);
    if (filter?.matchSource) params = params.set('matchSource', filter.matchSource);
    if (filter?.search)    params = params.set('search', filter.search);
    if (filter?.startDate) params = params.set('startDate', filter.startDate);
    if (filter?.endDate)   params = params.set('endDate', filter.endDate);

    return this.http.get<TraceARPaymentRecordsSummary>(
      `${this.baseUrl}/admin/tracear-payments/summary`,
      { headers: this.reqHeader, params }
    );
  }

  /** GET /admin/tracear-payments/:id — full raw charge + the recorded HELIX/Employer process behind it */
  getTraceARPaymentRecordDetail(id: number): Observable<TraceARPaymentRecordDetail> {
    return this.http.get<TraceARPaymentRecordDetail>(
      `${this.baseUrl}/admin/tracear-payments/${id}`,
      { headers: this.reqHeader }
    );
  }

  // ── Cash Reconciliation (HELIX + TraceAR + Bank, admin only) ─────────────

  /** GET /admin/bank-reconciliation/report — monthly + total, HELIX vs TraceAR vs Bank */
  getCashReconciliationReport(): Observable<CashReconciliationReport> {
    return this.http.get<{ success: boolean; data: CashReconciliationReport }>(
      `${this.baseUrl}/admin/bank-reconciliation/report`,
      { headers: this.reqHeader }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/report/pdf — download the executive Cash Reconciliation report as a PDF blob */
  downloadCashReconciliationPdf(): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/admin/bank-reconciliation/report/pdf`,
      { headers: this.reqHeader, responseType: 'blob' }
    );
  }

  /** GET /admin/bank-reconciliation/uploads — audit history of uploaded bank statement files */
  getBankStatementUploads(): Observable<BankStatementUploadSummary[]> {
    return this.http.get<{ success: boolean; data: BankStatementUploadSummary[] }>(
      `${this.baseUrl}/admin/bank-reconciliation/uploads`,
      { headers: this.reqHeader }
    ).pipe(map(r => r.data));
  }

  /** POST /admin/bank-reconciliation/uploads — upload a monthly bank statement CSV (deduped server-side) */
  uploadBankStatement(file: File): Observable<BankStatementUploadResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    // Multipart: no fijar Content-Type — el browser agrega el boundary.
    const headers = this.reqHeader.delete('Content-Type');
    return this.http.post<{ success: boolean; data: BankStatementUploadResult }>(
      `${this.baseUrl}/admin/bank-reconciliation/uploads`,
      form,
      { headers }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/lines — drill-down of parsed bank movements for a given month/category */
  getBankStatementLines(filter: { month?: number; year?: number; months?: { month: number; year: number }[]; category?: BankStatementCategory; scope?: 'expenses'; search?: string; page?: number; limit?: number }): Observable<BankStatementLinesPage> {
    let params = new HttpParams();
    if (filter.months?.length) params = params.set('months', monthsParam(filter.months));
    else {
      if (filter.month) params = params.set('month', filter.month);
      if (filter.year)  params = params.set('year', filter.year);
    }
    if (filter.category) params = params.set('category', filter.category);
    if (filter.scope)    params = params.set('scope', filter.scope);
    if (filter.search)   params = params.set('search', filter.search);
    if (filter.page)     params = params.set('page', filter.page);
    if (filter.limit)    params = params.set('limit', filter.limit);

    return this.http.get<{ success: boolean; data: BankStatementLinesPage }>(
      `${this.baseUrl}/admin/bank-reconciliation/lines`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** PATCH /admin/bank-reconciliation/lines/:id/category — manual category correction for one bank movement */
  setBankStatementLineCategory(id: number, category: BankStatementCategory): Observable<{ id: number; category: string; categorySource: string }> {
    return this.http.patch<{ success: boolean; data: { id: number; category: string; categorySource: string } }>(
      `${this.baseUrl}/admin/bank-reconciliation/lines/${id}/category`,
      { category },
      { headers: this.reqHeader }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/client-payments — HELIX + Employer System payments behind the "Client Payments via Platforms" KPI */
  getClientPaymentDetail(months: { month: number; year: number }[]): Observable<ClientPaymentDetail[]> {
    const params = new HttpParams().set('months', monthsParam(months));
    return this.http.get<{ success: boolean; data: ClientPaymentDetail[] }>(
      `${this.baseUrl}/admin/bank-reconciliation/client-payments`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/tracear-captures — records behind the TraceAR Gross KPI */
  getTracearCaptureDetail(months: { month: number; year: number }[]): Observable<TracearCaptureDetail[]> {
    const params = new HttpParams().set('months', monthsParam(months));
    return this.http.get<{ success: boolean; data: TracearCaptureDetail[] }>(
      `${this.baseUrl}/admin/bank-reconciliation/tracear-captures`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/vendor-breakdown — per-vendor amounts within each expense category */
  getVendorBreakdown(months: { month: number; year: number }[]): Observable<VendorBreakdown> {
    const params = new HttpParams().set('months', monthsParam(months));
    return this.http.get<{ success: boolean; data: VendorBreakdown }>(
      `${this.baseUrl}/admin/bank-reconciliation/vendor-breakdown`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/unmatched-tracear — TraceAR captures with no HELIX or Employer System invoice match */
  getUnmatchedTracearCaptures(months: { month: number; year: number }[]): Observable<UnmatchedTracearCapture[]> {
    const params = new HttpParams().set('months', monthsParam(months));
    return this.http.get<{ success: boolean; data: UnmatchedTracearCapture[] }>(
      `${this.baseUrl}/admin/bank-reconciliation/unmatched-tracear`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/bank-reconciliation/reconciliation-explanation — detailed breakdown of both
   *  "Client Payments via Platforms" gaps: vs. TraceAR Gross (KPI 1) and vs. Bank Net Settled (KPI 2) */
  getReconciliationExplanation(months: { month: number; year: number }[]): Observable<ReconciliationExplanation> {
    const params = new HttpParams().set('months', monthsParam(months));
    return this.http.get<{ success: boolean; data: ReconciliationExplanation }>(
      `${this.baseUrl}/admin/bank-reconciliation/reconciliation-explanation`,
      { headers: this.reqHeader, params }
    ).pipe(map(r => r.data));
  }

  /** GET /admin/business-intelligence/pdf — download the Business Intelligence executive report as a PDF blob */
  downloadBusinessIntelligencePdf(month: number, year: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/admin/business-intelligence/pdf?month=${month}&year=${year}`,
      { headers: this.reqHeader, responseType: 'blob' }
    );
  }

  /** GET /reports/collections/pdf — download the monthly collections report as a PDF blob */
  downloadCollectionsPdf(month: number, year: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}${this.REPORTS_SERVICE}/collections/pdf?month=${month}&year=${year}`,
      { headers: this.reqHeader, responseType: 'blob' }
    );
  }

  /** GET /reports/coverage/pdf — download the coverage report as a PDF blob */
  downloadCoveragePdf(month: number, year: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}${this.REPORTS_SERVICE}/coverage/pdf?month=${month}&year=${year}`,
      { headers: this.reqHeader, responseType: 'blob' }
    );
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // ADMIN  ·  /admin
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /admin/seller-requests — pending cancel/refund requests submitted by sellers for admin review */
  getSellerRequests(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.baseUrl}/admin/seller-requests`,
      { headers: this.reqHeader }
    )
  }

  /** POST /admin/seller-requests/:idTransactionHistory/process — approve or reject a seller cancellation/refund request */
  processSellerRequest(idTransactionHistory: number, approve: boolean, note: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/admin/seller-requests/${idTransactionHistory}/process`,
      { approve, note },
      { headers: this.reqHeader }
    )
  }


  // ── Outbox Monitor ──────────────────────────────────

  /** GET /admin/outbox/stats — summary counts for invoice and notification outboxes */
  getOutboxStats(): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/admin/outbox/stats`,
      { headers: this.reqHeader }
    )
  }

  /** GET /admin/outbox/invoices */
  getOutboxInvoices(params?: { status?: number; page?: number; limit?: number }): Observable<{ success: boolean; data: any }> {
    const parts: string[] = []
    if (params?.status !== undefined) parts.push(`status=${params.status}`)
    if (params?.page)                 parts.push(`page=${params.page}`)
    if (params?.limit)                parts.push(`limit=${params.limit}`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    return this.http.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/admin/outbox/invoices${qs}`,
      { headers: this.reqHeader }
    )
  }

  /** GET /admin/outbox/notifications */
  getOutboxNotifications(params?: { status?: number; page?: number; limit?: number }): Observable<{ success: boolean; data: any }> {
    const parts: string[] = []
    if (params?.status !== undefined) parts.push(`status=${params.status}`)
    if (params?.page)                 parts.push(`page=${params.page}`)
    if (params?.limit)                parts.push(`limit=${params.limit}`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    return this.http.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/admin/outbox/notifications${qs}`,
      { headers: this.reqHeader }
    )
  }

  /** GET /admin/outbox/invoices/:id/attempts — full attempt history for one outbox row */
  getOutboxInvoiceAttempts(id: number): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.baseUrl}/admin/outbox/invoices/${id}/attempts`,
      { headers: this.reqHeader }
    )
  }

  /** POST /admin/outbox/invoices/:id/retry — reset a FAILED invoice outbox row to PENDING */
  retryOutboxInvoice(id: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/admin/outbox/invoices/${id}/retry`,
      {},
      { headers: this.reqHeader }
    )
  }

  /** POST /admin/outbox/notifications/:id/retry — reset a FAILED notification outbox row to PENDING */
  retryOutboxNotification(id: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/admin/outbox/notifications/${id}/retry`,
      {},
      { headers: this.reqHeader }
    )
  }

  // ── Provider Report Tracking ────────────────────────

  /** GET /admin/provider-tracking — clients reported to providers + credential lifecycle */
  getProviderTracking(params?: {
    reportStatus?: string;
    credentialStatus?: string;
    idProvider?: number;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Observable<{ success: boolean; stats: any; total: number; page: number; pageSize: number; rows: any[] }> {
    const parts: string[] = []
    if (params?.reportStatus)      parts.push(`reportStatus=${params.reportStatus}`)
    if (params?.credentialStatus)  parts.push(`credentialStatus=${params.credentialStatus}`)
    if (params?.idProvider)        parts.push(`idProvider=${params.idProvider}`)
    if (params?.search?.trim())    parts.push(`search=${encodeURIComponent(params.search.trim())}`)
    if (params?.page)              parts.push(`page=${params.page}`)
    if (params?.pageSize)          parts.push(`pageSize=${params.pageSize}`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    return this.http.get<{ success: boolean; stats: any; total: number; page: number; pageSize: number; rows: any[] }>(
      `${this.baseUrl}/admin/provider-tracking${qs}`,
      { headers: this.reqHeader }
    )
  }

  /** POST /admin/provider-tracking/reports/:outboxId/mark-delivered — manual delivery confirmation (audited) */
  markProviderReportDelivered(outboxId: number): Observable<{ success: boolean; paymentPromoted?: boolean }> {
    return this.http.post<{ success: boolean; paymentPromoted?: boolean }>(
      `${this.baseUrl}/admin/provider-tracking/reports/${outboxId}/mark-delivered`,
      {},
      { headers: this.reqHeader }
    )
  }

  /** POST /admin/provider-tracking/credentials — record a provider credential (multipart) for one covered
   *  member (idDependent 0 = primary client). The client email goes out once the provider's full set
   *  of member credentials is recorded (emailPending = true while members are still missing), unless
   *  sendEmail is false — in that case the credential is saved but no email fires (emailSkipped = true);
   *  send it later via resendProviderCredential. */
  recordProviderCredential(data: {
    idProductTransaction: number;
    idProvider: number;
    idDependent?: number;
    credentialId: string;
    file?: File | null;
    sendEmail?: boolean;
  }): Observable<{ success: boolean; credential: any; emailSent: boolean; emailPending: boolean; emailSkipped: boolean; emailError: string | null; paymentPromoted: boolean }> {
    const form = new FormData()
    form.append('idProductTransaction', String(data.idProductTransaction))
    form.append('idProvider', String(data.idProvider))
    form.append('idDependent', String(data.idDependent ?? 0))
    form.append('credentialId', data.credentialId)
    form.append('sendEmail', String(data.sendEmail ?? true))
    if (data.file) form.append('file', data.file, data.file.name)
    // Multipart: no fijar Content-Type — el browser agrega el boundary.
    const headers = this.reqHeader.delete('Content-Type')
    return this.http.post<{ success: boolean; credential: any; emailSent: boolean; emailPending: boolean; emailSkipped: boolean; emailError: string | null; paymentPromoted: boolean }>(
      `${this.baseUrl}/admin/provider-tracking/credentials`,
      form,
      { headers }
    )
  }

  /** POST /admin/provider-tracking/credentials/:id/resend — resend the provider's credential email
   *  (all member credentials bundled) to the client */
  resendProviderCredential(credentialRowId: number): Observable<{ success: boolean; paymentPromoted?: boolean }> {
    return this.http.post<{ success: boolean; paymentPromoted?: boolean }>(
      `${this.baseUrl}/admin/provider-tracking/credentials/${credentialRowId}/resend`,
      {},
      { headers: this.reqHeader }
    )
  }

  /** GET /admin/provider-tracking/credentials/:id/file — download the credential PDF */
  downloadProviderCredential(credentialRowId: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/admin/provider-tracking/credentials/${credentialRowId}/file`,
      { headers: this.reqHeader, responseType: 'blob' }
    )
  }

  // ── Provider Financials ─────────────────────────────

  /** GET /admin/provider-financials — clients, revenue, owed (estimate) and paid per provider, monthly + total */
  getProviderFinancials(): Observable<ProviderFinancialsRow[]> {
    return this.http.get<{ success: boolean; providers: ProviderFinancialsRow[] }>(
      `${this.baseUrl}/admin/provider-financials`,
      { headers: this.reqHeader }
    ).pipe(map(r => r.providers));
  }

  /** GET /admin/provider-financials/:idProvider/clients — clients currently enrolled with this provider */
  getProviderFinancialsClients(idProvider: number): Observable<ProviderFinancialsClient[]> {
    return this.http.get<{ success: boolean; clients: ProviderFinancialsClient[] }>(
      `${this.baseUrl}/admin/provider-financials/${idProvider}/clients`,
      { headers: this.reqHeader }
    ).pipe(map(r => r.clients));
  }


  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // SELLER  ·  /seller
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /seller/payment-tracker — paginated payment pipeline for the authenticated seller's portfolio */
  getSellerPaymentTracker(params?: {
    page?: number;
    limit?: number;
    statusFilter?: string[];
    clientSearch?: string;
    sort?: string;
    onlyFailed?: boolean;
  }): Observable<PaymentTrackerResponse> {
    const parts: string[] = []
    if (params?.page)                 parts.push(`page=${params.page}`)
    if (params?.limit)                parts.push(`limit=${params.limit}`)
    if (params?.statusFilter?.length) parts.push(`statusFilter=${params.statusFilter.join(',')}`)
    if (params?.clientSearch?.trim()) parts.push(`clientSearch=${encodeURIComponent(params.clientSearch.trim())}`)
    if (params?.sort)                 parts.push(`sort=${params.sort}`)
    if (params?.onlyFailed)           parts.push(`onlyFailed=true`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    return this.http.get<PaymentTrackerResponse>(`${this.baseUrl}/seller/payment-tracker${qs}`, { headers: this.reqHeader })
  }

  /** POST /seller/payment-tracker/:idPayment/request-reconciliation — seller flags a payment for admin reconciliation review */
  requestPaymentReconciliation(idPayment: number, reason?: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/seller/payment-tracker/${idPayment}/request-reconciliation`,
      { reason: reason?.trim() || '' },
      { headers: this.reqHeader }
    )
  }

  /** GET /seller/messages — paginated inbox of seller notifications (cancel/refund approvals, system alerts) */
  getSellerMessages(params: { page?: number; limit?: number; unreadOnly?: boolean } = {}): Observable<MessagesResponse> {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.set('page', params.page.toString());
    if (params.limit !== undefined) q.set('limit', params.limit.toString());
    if (params.unreadOnly) q.set('unreadOnly', 'true');
    return this.http.get<MessagesResponse>(`${this.baseUrl}${this.SELLER_SERVICE}/messages?${q}`, { headers: this.reqHeader });
  }

  /** GET /seller/messages/unread-count — badge count of unread seller messages */
  getSellerUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(`${this.baseUrl}${this.SELLER_SERVICE}/messages/unread-count`, { headers: this.reqHeader });
  }

  /** POST /seller/messages/:idTransactionHistory/read — mark a single seller message as read */
  markSellerMessageRead(idTransactionHistory: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}${this.SELLER_SERVICE}/messages/${idTransactionHistory}/read`,
      {},
      { headers: this.reqHeader }
    );
  }

  /** POST /seller/messages/read-all — mark all unread seller messages as read */
  markAllSellerMessagesRead(): Observable<{ success: boolean; marked: number }> {
    return this.http.post<{ success: boolean; marked: number }>(
      `${this.baseUrl}${this.SELLER_SERVICE}/messages/read-all`,
      {},
      { headers: this.reqHeader }
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Chat de ayuda (HELIX help chat)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /** GET /chat/status — whether the help chat widget is currently active (HELPER_CHAT_ACTIVE env var) */
  getChatStatus(): Observable<{ success: boolean; active: boolean }> {
    return this.http.get<{ success: boolean; active: boolean }>(
      `${this.baseUrl}${this.CHAT_SERVICE}/status`,
      { headers: this.reqHeader }
    );
  }

  /** POST /chat/ask — ask the HELIX help chat a question, answered from the curated knowledge base */
  askChat(message: string): Observable<{ success: boolean; reply: string }> {
    return this.http.post<{ success: boolean; reply: string }>(
      `${this.baseUrl}${this.CHAT_SERVICE}/ask`,
      { message },
      { headers: this.reqHeader }
    );
  }

}
